import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

const MIN_WIDTH = 48;

/**
 * Drag-to-resize table columns. Widths are unset until the first drag; then
 * every column is pinned to its measured width so the table switches to a
 * fixed layout without any column jumping.
 */
export function useColumnResize() {
  const [widths, setWidths] = useState<number[] | null>(null);
  const headRef = useRef<HTMLTableRowElement | null>(null);

  const startResize = useCallback((index: number, event: ReactPointerEvent<HTMLElement>) => {
    const row = headRef.current;
    if (!row) return;
    event.preventDefault();
    const cells = Array.from(row.children) as HTMLElement[];
    const start = cells.map((cell) => cell.getBoundingClientRect().width);
    const startX = event.clientX;
    setWidths(start);

    const onMove = (e: PointerEvent) => {
      const next = start.slice();
      next[index] = Math.max(MIN_WIDTH, start[index] + e.clientX - startX);
      setWidths(next);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  const tableProps = widths
    ? { className: "crm-table crm-table--resizable", style: { width: widths.reduce((a, b) => a + b, 0) } }
    : { className: "crm-table crm-table--resizable" };

  const thProps = (index: number) => ({
    style: widths ? { width: widths[index] } : undefined,
  });

  const handle = (index: number) => (
    <span
      className="crm-col-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      onPointerDown={(e) => startResize(index, e)}
    />
  );

  return { headRef, tableProps, thProps, handle };
}
